import { useEffect } from 'react';
import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import '../components/landing.css';

// Factual service guidance; contract decisions stay in docs/marketing-legal-review.md.
export function TermsPage() {
  useEffect(() => {
    document.title = 'Using Paddiq | Paddiq';
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />
      <main id="main">
        <section className="policy-hero">
          <div className="landing-container policy-hero__inner">
            <p className="section-kicker">Service information</p>
            <h1>Using Paddiq</h1>
            <p className="policy-hero__lead">
              How accounts, connected services and paid features work in Paddiq.
            </p>
          </div>
        </section>
        <section className="policy-section">
          <div className="landing-container policy-layout">
            <aside className="policy-summary" aria-label="Service information navigation">
              <h2>On this page</h2>
              <ul>
                <li>
                  <a href="#service">Paddiq and its services</a>
                </li>
                <li>
                  <a href="#authority">Accounts and authority</a>
                </li>
                <li>
                  <a href="#connected-services">Connected AI services</a>
                </li>
                <li>
                  <a href="#paid-features">Paid features</a>
                </li>
                <li>
                  <a href="#responsible-use">Responsible use</a>
                </li>
                <li>
                  <a href="#privacy-and-help">Privacy and help</a>
                </li>
              </ul>
            </aside>
            <article className="policy-content">
              <section id="service">
                <h2>1. Paddiq and its services</h2>
                <p>
                  Paddiq LLC provides the Paddiq race-data and race-operations services. Paddiq
                  helps drivers, families, coaches, mechanics and teams organize sessions, review
                  telemetry and recordings, compare eligible laps, and manage vehicle and setup
                  information. Available features depend on the service, account permissions and
                  applicable entitlements.
                </p>
              </section>
              <section id="authority">
                <h2>2. Accounts and authority</h2>
                <p>
                  Your account permissions determine which workspaces and records you can access. A
                  connected service uses that same access, together with the permissions you grant
                  to the connection. Keep your sign-in details private.
                </p>
                <p>
                  Review the target and consequences before changing assignments, access or records.
                  Some actions can affect other workspace members, move shared data, send
                  invitations or permanently delete records.
                </p>
              </section>
              <section id="connected-services">
                <h2>3. Connected AI services</h2>
                <p>
                  If you choose to connect Paddiq to ChatGPT or another supported AI service, sign
                  in to Paddiq and review the requested access. The connection can return
                  information permitted by your current Paddiq access and the permissions you grant.
                  Tool results are shared with the service making the authorized request. Its
                  handling of information is governed by its own terms and privacy controls.
                </p>
                <p>
                  Supported changes require enabled features, the relevant permissions and a preview
                  of the requested action. Some routine edits can execute from chat; sensitive
                  actions, including deletion, also require approval of that exact action in
                  signed-in Paddiq. A chat confirmation cannot grant missing permissions or replace
                  that approval. Platform-operator and campaign access are not part of the public
                  Paddiq connection.
                </p>
                <p>
                  You can disconnect in Paddiq Settings under Connections. Disconnecting stops
                  future access through that connection; it does not undo completed changes or
                  remove information already delivered to the connected service.
                </p>
              </section>
              <section id="paid-features">
                <h2>4. Paid features</h2>
                <p>
                  Purchases are completed outside ChatGPT through Paddiq&apos;s web checkout, which
                  uses Stripe. The Paddiq mobile app can also open that web checkout. The public
                  ChatGPT connection does not provide purchases, change billable seats or initiate
                  telemetry uploads. Current GoPro processing through this connection does not
                  create a Stripe charge.
                </p>
              </section>
              <section id="responsible-use">
                <h2>5. Responsible use and service limits</h2>
                <p>
                  Keep passwords, payment-card details and other authentication secrets out of
                  telemetry notes and chat requests. Use Paddiq&apos;s sign-in and checkout screens
                  for account access and payments.
                </p>
                <p>
                  Telemetry, recordings and AI-generated explanations depend on the available data
                  and can be incomplete or incorrect. Check timing sources, missing channels,
                  eligibility and the underlying records before relying on a comparison. A change in
                  recorded speed alone does not establish braking input or the cause of a lap
                  difference. Paddiq does not remotely operate a kart or engine.
                </p>
              </section>
              <section id="privacy-and-help">
                <h2>6. Privacy and help</h2>
                <p>
                  Read the <a href="/privacy">Paddiq privacy policy</a> for information about data
                  practices and your choices. Contact <a href="/support">Paddiq support</a> or{' '}
                  <a href="mailto:support@paddiq.app">support@paddiq.app</a> for help with
                  Paddiq or your account.
                </p>
              </section>
            </article>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
